import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { navigate } from 'gatsby'

import { login as apiLogin, logout as apiLogout } from 'apiClient'
import { siteMetadata } from 'config'
import { hasWindow, getFromStorage, saveToStorage } from 'util/dom'

const { googleAnalyticsId } = siteMetadata

const AuthStateContext = createContext()
const AuthDispatchContext = createContext()

export const Provider = ({ children }) => {
  const initialState =
    hasWindow && window.localStorage ? getFromStorage('auth') || {} : {}
  let {
    id: initialId = '',
    name: initialName = '',
    isAdmin: initialIsAdmin = false,
    expires: initialExpires = null,
  } = initialState

  if (
    initialId !== '' &&
    initialExpires !== null &&
    new Date(initialExpires).getTime() - new Date().getTime() > 0
  ) {
    // config google analytics with this user
    if (hasWindow && window.gtag && googleAnalyticsId) {
      window.gtag('set', 'user_id', initialId)
    }
  } else {
    // session is expired, clear outdated auth
    initialId = ''
    initialName = ''
    initialIsAdmin = false
    initialExpires = null
    if (hasWindow && window.localStorage) {
      window.localStorage.removeItem('auth')
    }
  }

  const [state, setState] = useState({
    isLoggedIn: initialId !== '',
    id: initialId,
    name: initialName,
    isAdmin: initialIsAdmin,
    expires: initialExpires,
  })

  const login = useCallback(async (userId, password) => {
    const response = await apiLogin(userId, password)
    if (response.error) {
      return response
    }

    const { id = '', name = '', isAdmin = false, expires = null } = response
    if (id !== '') {
      setState(() => ({
        isLoggedIn: true,
        id,
        name,
        isAdmin,
        expires,
      }))

      saveToStorage('auth', { id, name, isAdmin, expires })

      // config google analytics with this user
      if (hasWindow && window.gtag && googleAnalyticsId) {
        window.gtag('set', 'user_id', id)
      }
    }

    return { error: null, isLoggedIn: true, id }
  }, [])

  const logout = useCallback(async () => {
    await apiLogout()

    setState(() => ({
      isLoggedIn: false,
      id: '',
      name: '',
      isAdmin: false,
      expires: null,
    }))

    if (hasWindow && window.localStorage) {
      window.localStorage.removeItem('auth')
    }

    if (hasWindow && window.gtag && googleAnalyticsId) {
      window.gtag('set', 'user_id', null)
    }

    navigate('/')
  }, [])

  const authFunctions = useMemo(
    () => ({
      login,
      logout,
    }),
    [login, logout]
  )

  // We use nested context providers below to split up the parts of the context that are changing frequently (the state)
  // from the parts that are not changing once defined (the callback functions)
  return (
    <AuthDispatchContext.Provider value={authFunctions}>
      <AuthStateContext.Provider value={state}>
        {children}
      </AuthStateContext.Provider>
    </AuthDispatchContext.Provider>
  )
}

Provider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAuth = () => ({
  ...useContext(AuthDispatchContext),
  ...useContext(AuthStateContext),
})
